/**
 * ESIC view component.
 * @module components/Views/ESICView
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Container } from '@plone/components';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasBlocksData } from '@plone/volto/helpers/Blocks/Blocks';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import ExternalDataForm from '../ExternalDataForm/ExternalDataForm';
import ExternalDataResults from '../ExternalDataResults/ExternalDataResults';
import NextPrevious from '../NextPrevious/NextPrevious';
import PieChart from '../PieChart/PieChart';
import { processColumns } from '../../helpers/columns';
import logoCMR from '../../components/Logo/Logo-Black.png';

const ESICSummary = ({ total, status, faixa_etaria }) => {
  return (
    total &&
    faixa_etaria &&
    status && (
      <Container narrow className={'esic-sumario'}>
        <h3 className="header">Sumário</h3>
        <Container className="colunas">
          <Container className="grafico coluna">
            <PieChart data={status} />
          </Container>
          <Container className="legenda coluna">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Situação</th>
                  <th>Pedidos</th>
                </tr>
              </thead>
              <tbody>
                {status.map((item, idx) => {
                  return (
                    <tr key={idx}>
                      <td
                        className="label"
                        style={{ backgroundColor: item.color }}
                      ></td>
                      <td className="title">{item.title}</td>
                      <td className="value">{item.value}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Container>
        </Container>
        <Container className="colunas">
          <Container className="grafico coluna">
            <PieChart data={faixa_etaria} />
          </Container>
          <Container className="legenda coluna">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Faixa Etária</th>
                  <th>Pedidos</th>
                </tr>
              </thead>
              <tbody>
                {faixa_etaria.map((item, idx) => {
                  return (
                    <tr key={idx}>
                      <td
                        className="label"
                        style={{ backgroundColor: item.color }}
                      ></td>
                      <td className="title">{item.title}</td>
                      <td className="value">{item.value}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Container>
        </Container>
      </Container>
    )
  );
};

const ESICView = (props) => {
  const { content, location } = props;
  const allowedTypes = [];
  const path = getBaseUrl(location?.pathname || '');
  const { title, description, data, view_config, display_form } = content;
  const [showForm, setShowForm] = useState(display_form);
  const [localData, setLocalData] = useState();
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setLocalData(data);
    setHasError(!!data?.error);
  }, [data, setLocalData]);

  const displayForm = () => {
    setShowForm(true);
    setLocalData({});
  };

  const setLocalFormData = (data) => {
    if (data?.data?.items) {
      setHasError(!!data?.error);
      setLocalData(data);
      setShowForm(false);
    } else if (data?.inserido) {
      setLocalData({ ...data, data: { items: [], total: 0 } });
      setShowForm(false);
    }
  };
  const pagination = view_config?.display?.pagination;
  const paginationPerPage = pagination?.paginationPerPage;
  const columns = processColumns(view_config?.display?.columns);
  const expandRows = view_config?.display?.expandRows;
  const expandRowsType = view_config?.display?.expandRowsType;
  const metadata = localData?.metadata;
  // Se for resultado de uma inserção, ocultamos os "resultados"
  const respostaInsercao = localData?.inserido || false;
  const msgNoResults = {
    no_results: hasError
      ? localData.error?.msg
      : respostaInsercao
        ? ''
        : 'Nenhum resultado encontrado',
    no_results_ex: '',
  };
  return (
    <Container id="page-document" className="view-wrapper esic-view">
      {hasBlocksData(content) ? (
        <RenderBlocks {...props} path={path} />
      ) : (
        <div className={'cabecalho'}>
          <h1 className="documentFirstHeading">{title}</h1>
          {description && <p className="documentDescription">{description}</p>}
        </div>
      )}
      {!showForm && metadata && (
        <>
          <ExternalDataResults
            data={localData.data}
            metadata={metadata}
            columns={columns}
            showForm={displayForm}
            display_form={display_form}
            expandRows={expandRows}
            expandRowsType={expandRowsType}
            paginationPerPage={paginationPerPage}
            msgNoResults={msgNoResults}
            signature={{
              link: '"https://www.recife.leg.br/',
              image: logoCMR,
              alt: 'Logo Câmara Municipal do Recife',
            }}
          >
            {localData.summary && <ESICSummary {...localData.summary} />}
          </ExternalDataResults>
        </>
      )}
      {showForm && (
        <ExternalDataForm
          {...props}
          title={'e-SIC'}
          setLocalFormData={setLocalFormData}
        />
      )}
      <NextPrevious content={content} allowedTypes={allowedTypes} />
    </Container>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
ESICView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
};

export default ESICView;
